// src/clientIdentity.js
import { useEffect, useState } from "react";
import { collection, query, where } from "firebase/firestore";
import { getDocs } from "./queryMonitor"; // timed reads (Billing → Performance)
import { db } from "./firebase";

const EMAIL_KEY = "dance_booking_email";
const ACCOUNT_KEY = "dance_booking_account";
const CHANGE_EVENT = "dance-booking-email-changed";

export function getClientAccount() {
  try { return JSON.parse(localStorage.getItem(ACCOUNT_KEY)); } catch { return null; }
}

// Sets (or clears) the matched studio account and notifies useClientAccount()
// hooks in this tab, e.g. so the nav can un-grey account-only tabs.
export function setClientAccount(account) {
  if (account) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  else localStorage.removeItem(ACCOUNT_KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function useClientAccount() {
  const [account, setAccount] = useState(getClientAccount);

  useEffect(() => {
    const handler = () => setAccount(getClientAccount());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return account;
}

export function getClientEmail() {
  return localStorage.getItem(EMAIL_KEY) || "";
}

// Sets (or clears, when email is falsy) the client's stored email and notifies
// any mounted useClientEmail() hooks in this tab — a plain localStorage write
// alone only triggers the "storage" event in *other* tabs.
export function setClientEmail(email) {
  if (email) localStorage.setItem(EMAIL_KEY, email);
  else localStorage.removeItem(EMAIL_KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

// Looks an account up by either its primary or secondary email. Two separate
// single-field queries (rather than a Firestore `or()`) so no composite index
// is required.
export async function findAccountByEmail(email) {
  const e = email.toLowerCase();
  const [byPrimary, bySecondary] = await Promise.all([
    getDocs(query(collection(db, "accounts"), where("email", "==", e))),
    getDocs(query(collection(db, "accounts"), where("secondaryEmail", "==", e))),
  ]);
  const snap = !byPrimary.empty ? byPrimary : bySecondary;
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export function useClientEmail() {
  const [email, setEmail] = useState(getClientEmail);

  useEffect(() => {
    const handler = () => setEmail(getClientEmail());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return email;
}
